// Global notifier for the internal team chat: plays a sound and raises a browser notification for
// incoming messages, even when the user is on another dashboard screen. Wired once in Dashboard.vue.
// The page registers itself so the currently-open chat is never notified, and to own the sound while
// mounted. Preference lives in uiSettings (per user); sound reuses the existing chat sound key.
import { onBeforeUnmount, onMounted } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

export const NOTIF_PREF_KEY = 'wasapi_internal_chat_notifications_enabled';
const SOUND_KEY = 'chatSoundEnabled';
const TAG = 'wasapi-internal-chat-';
const AUTO_CLOSE_MS = 8000;
const DEDUPE = [];

let activeChatId = null;
let pendingChatId = null;
let audio = null;

export const setActiveInternalChatId = id => {
  activeChatId = id ?? null;
};
export const consumePendingInternalChatId = () => {
  const id = pendingChatId;
  pendingChatId = null;
  return id;
};
export const getNotificationPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window))
    return 'unsupported';
  return window.Notification.permission;
};
export const requestNotificationPermission = async () => {
  if (getNotificationPermission() === 'unsupported') return 'unsupported';
  try {
    return await window.Notification.requestPermission();
  } catch (e) {
    return getNotificationPermission();
  }
};

// The user is "actively viewing" only when the tab is visible AND focused. A chat left open in a
// backgrounded tab must still alert (sound + notification), and must NOT be auto-marked as read.
export const isDocumentFocused = () => {
  try {
    return !document.hidden && document.hasFocus();
  } catch (e) {
    return true;
  }
};

// Pure decision for a realtime message — kept side-effect free so it is unit-testable.
// Returns { sound, browser } booleans.
export const evaluateNotification = ({
  message,
  currentUserId,
  activeChatId: openChatId,
  chatId,
  focused,
  notifEnabled,
  permission,
}) => {
  if (!message || message.deleted) return { sound: false, browser: false };
  if (message.user_id === currentUserId)
    return { sound: false, browser: false };
  // Suppress entirely only when the user is genuinely looking at this chat.
  if (chatId === openChatId && focused) return { sound: false, browser: false };
  return {
    sound: true,
    browser: notifEnabled !== false && permission === 'granted',
  };
};

const soundEnabled = () => localStorage.getItem(SOUND_KEY) !== 'false';
const playSound = () => {
  if (!soundEnabled()) return;
  if (!audio) {
    audio = new Audio('/audio/dashboard/ding.mp3');
    audio.load();
  }
  audio.play().catch(() => {});
};

const dedupe = id => {
  if (!id || DEDUPE.includes(id)) return false;
  DEDUPE.push(id);
  if (DEDUPE.length > 60) DEDUPE.shift();
  return true;
};

// Global singleton wiring — call once from the app shell.
export function useInternalChatGlobalNotifier() {
  const currentUser = useMapGetter('getCurrentUser');
  const { uiSettings } = useUISettings();

  const onMessage = data => {
    const message = data?.message;
    const decision = evaluateNotification({
      message,
      currentUserId: currentUser.value?.id,
      activeChatId,
      chatId: data?.chat_id,
      focused: isDocumentFocused(),
      notifEnabled: uiSettings.value?.[NOTIF_PREF_KEY],
      permission: getNotificationPermission(),
    });
    if (!decision.sound && !decision.browser) return;
    if (!dedupe(`${data.chat_id}-${message.id}`)) return;

    if (decision.sound) playSound();
    if (!decision.browser) return;

    const title = data.chat_name || message.user_name;
    const body = message.content
      ? `${message.user_name}: ${message.content}`.slice(0, 140)
      : message.user_name;
    try {
      const notification = new window.Notification(title, {
        body,
        tag: `${TAG}${data.chat_id}`,
        renotify: true,
      });
      notification.onclick = () => {
        window.focus();
        pendingChatId = data.chat_id;
        emitter.emit('wasapi:internal_chat:open', data.chat_id);
        notification.close();
      };
      setTimeout(() => notification.close(), AUTO_CLOSE_MS);
    } catch (e) {
      // ignore
    }
  };

  onMounted(() => emitter.on('wasapi:internal_chat:message', onMessage));
  onBeforeUnmount(() => emitter.off('wasapi:internal_chat:message', onMessage));
}
